import  { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { typeSettingConfig } from "./typeSettingConfig";
import usePageState from "../../../../utils/customHooks/usePageState";
import { deleteApi, getApi, postApi } from "../../../../utils/services";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { DETAULT_ROLE, EMPLOYEE_ROLE, STAUTES } from "../../../../utils/constant";
import usePermission from "../../../../utils/customHooks/usePermission";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { R_SETTINGS } from "../../../../utils/permissionReferenceConfig";
import { useTheme } from "../../../ContextStore/ThemeContext";
import { useDispatch, useSelector } from "react-redux";
import { handleLogout, userSelector } from "../../../store/features/auth/authSlice";
import logo from "../../../Assets/Icons/logo.png";
import logoWhite from "../../../Assets/Icons/logo-white.png";
import menuopen from "../../../Assets/Icons/menu-open.png";
import menuclose from "../../../Assets/Icons/menu-close.png";
import NotificationIcon from "../../../Assets/IconComponents/Notification";
import { headerMenus } from "../../../../utils/MenusConfig";
import HelpMenu from "../../../Components/Header/HelpMenu";
import {ProGrid, NameCell, DeleteCell, Layout} from "@proscore-ai/proscore-ui-library";
import useCsvExport from "../../../../utils/useCsvExporter";

export default function TypeSettingList() {
  const { settingType } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [popOverOpen, setPopOverOpen] = useState(false);
  const ref = useRef(null);
  const userData = useSelector(userSelector);
  const dispatch = useDispatch();

  const handleTabClick = tab => {
    navigate(`${tab.to}`);
  };
  const handleMenuOpen = () => setMenuOpen(!menuOpen);

  const onLogout = () => {
    dispatch(handleLogout());
  };

  const manageAccount = () => {
    setPopOverOpen(false);
  };

  const handleHome = () => {
    navigate("/clients");
  };

  const { typeKey, typeLabel, idKey, gridKey, getApiPath, deleteApiPath, subModuleRef, listFormName } =  typeSettingConfig[settingType] || {};

  const {
    currentPage,
    pageSize,
    data: typeData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();

  const { [listFormName]: listPermission } = usePermission(subModPermissionSelector(R_SETTINGS, subModuleRef), listFormName);
  const { canView, canDelete, canAdd, canExport } = listPermission;

  const getRedirectUrl = useCallback(params => `/settings/${settingType}/${params.id}`, [settingType]);

  const addButtonAction = () => {
    navigate(`/settings/${settingType}/add`);
  };

  const getTypeData = useCallback(
    async typeKey => {
      setStatus(STAUTES.LOADING);
      setPageState(prevPageInfo => ({ ...prevPageInfo, data: [], rowCount: 0 }));
      const payload = {
        pageIndex: searchString ? 1 : currentPage + 1,
        pageSize: pageSize,
        searchString: searchString,
        orderByAscending: true,
        orderCol: typeKey,
        ...sortModel
      };
      const response = await postApi(getApiPath, payload);
      setStatus(STAUTES.IDLE);
      if (response.error) {
        const errorMessage = response.error.message;
        return errorToast(errorMessage);
      }
      setPageState(prevPageInfo => ({ ...prevPageInfo, data: response.data || [], rowCount: response.totalRecords }));
    },
    [currentPage, pageSize, sortModel, searchString, setPageState, setStatus, getApiPath]
  );

  const handleDownloadCSV = useCsvExport({
    api: getApiPath,

    getPayload: () => {
      const activeSort = sortModel?.[0] || {
        field: typeKey,
        sort: "asc"
      };

      return {
        pageIndex: 1,
        pageSize: rowCount || 10000,
        searchString,
        orderByAscending: activeSort.sort === "asc",
        orderCol: activeSort.field
      };
    },

    mapData: data =>
      data.map(item => ({
        [typeLabel]: item[typeKey] || ""
      })),

    fileName: `${typeLabel.replace(/\s+/g, "")}.csv`
  });
  
  const onDelete = useCallback(
    async (id) => {
      if (!id) return errorToast("Invalid ID provided for deletion.");
      const idParam = deleteApiPath.includes("DeleteClientDocumentType") ? "clientId" : "clientContractTypeId";
      const apiUrl = `${deleteApiPath}?${idParam}=${id}`;  
      const response = await deleteApi(apiUrl);
      
      if (response.error) {
        const errorMessage = response.error.message || "Failed to delete item.";
        return errorToast(errorMessage);
      }
  
      getTypeData(typeKey);
      successToast(`${typeLabel} deleted successfully.`);
    },
    [typeLabel, idKey, deleteApiPath, getTypeData]
  );    
  

  useEffect(() => {
    if (typeKey) getTypeData(typeKey);
    else navigate("/");
  }, [typeKey, getTypeData, navigate]);

  const columns = useMemo(
    () => [
      {
        field: typeKey,
        headerName: "Title",
        renderCell: params => <NameCell params={params} getRedirectUrl={getRedirectUrl} />
      },
//...(canDelete
      //  ? [
            {
              field: "id",
              headerName: "Actions",
              isDeleteColumn: true,
              disableExport: true,
              width: 80,
              renderCell: params => {
                if (typeKey === "roleTypeName" && (params.row.roleTypeName === DETAULT_ROLE || params.row.roleTypeName === EMPLOYEE_ROLE))
                  return null;
                return <DeleteCell params={params} onDelete={onDelete} />;
              }
            }
         // ]
       // : [])
    ],
    [typeKey, getRedirectUrl, onDelete]
  );

  return (
    <Layout
      theme={theme}
      user={{
        userName: userData?.userName,
        email: userData?.email,
        avatar: userData?.avatar
      }}
      onLogout={onLogout}
      onTabClick={handleTabClick}
      onLogoClick={handleHome}
      logo={logo}
      logoWhite={logoWhite}
      menuOpenIcon={menuopen}
      menuCloseIcon={menuclose}
      menuTabs={headerMenus}
      NotificationIcon={NotificationIcon}
      HelpComponent={HelpMenu}
    >
      <ProGrid
        title={typeLabel}
        columns={columns}
        loading={status === STAUTES.LOADING}
        rows={typeData}
        searchMode={!!searchString}
        searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
        addButtonLabel={`+ Add ${typeLabel}`}
        addButtonAction={addButtonAction}
        //hideAddButton={!canAdd}
        //hideExport={!canExport}
        options={{
          getRowId: row => row[gridKey || idKey],
          rowCount: rowCount,
          paginationMode: "server",
          paginationModel: { pageSize: pageSize, page: currentPage },
          onPaginationModelChange: setPagination,
          sortingMode: "server",
          onSortModelChange: setSortModel
        }}
        exportAction={handleDownloadCSV}
      />
    </Layout>
  );
}
